package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initTheScreenshotIsTheTestPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/TheScreenshotIsTheTest.md", FrontMatterElement.Builder { addScalar("title", "The screenshot in the README is the test"); addScalar("searchTitle", "Kotlin Multiplatform full-stack demo — Compose clients and a native Ktor server"); addScalar("date", "2026-02-08"); addScalar("description", "mani is a Kotlin Multiplatform demo where the same server compiles to the JVM and to a native binary — and where the picture advertising the interface cannot drift from it."); addScalar("library", "mani"); }))
}

@Page("/blog/the-screenshot-is-the-test")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun TheScreenshotIsTheTestPage() {
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/mani") {
            org.jetbrains.compose.web.dom.Text("mani")
        }
        org.jetbrains.compose.web.dom.Text(" is a budget planner, and it is a demo on purpose. It exists to show what a full Kotlin stack looks like when every part of it is real rather than sketched: Compose Multiplatform clients for Android, iOS, desktop and the browser, a Ktor server that compiles ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("both")
        }
        org.jetbrains.compose.web.dom.Text(" to the JVM and to a native Linux binary, and one shared module holding the API contract for all of them.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-same-code-on-both-sides-of-the-wire") }) {
        org.jetbrains.compose.web.dom.Text("The same code on both sides of the wire")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The interesting part of KMP is not that clients share code. It is that the contract does.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The same ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Resource")
        }
        org.jetbrains.compose.web.dom.Text(" classes route requests on the server and build URLs on the client. Rename a path and both ends move together; there is no place for a string on one side to disagree with a string on the other, because there is only one string.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The same code signs a token in the JVM build and in the native build. That is a stronger claim than it looks: two server binaries, two runtimes, one implementation of the thing that must not differ between them.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-screenshot-cannot-lie") }) {
        org.jetbrains.compose.web.dom.Text("The screenshot cannot lie")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The image at the top of the README is not a screenshot somebody took and committed.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("is")
        }
        org.jetbrains.compose.web.dom.Text(" one of the goldens the screenshot tests compare against, referenced from its path in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("composeApp/src/desktopTest/snapshots")
        }
        org.jetbrains.compose.web.dom.Text(". Re-recording the goldens redraws the README picture in the same commit.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This is a small trick with a disproportionate effect. Every project's README screenshot starts accurate and ends up eighteen months stale, showing a screen that no longer exists. Pointing the README at the golden makes staleness impossible: if the picture is old, the tests are failing.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The goldens come from ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/viddik") {
            org.jetbrains.compose.web.dom.Text("viddik")
        }
        org.jetbrains.compose.web.dom.Text(", which renders through Compose Desktop, so they hold across machines.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("two-of-its-dependencies-were-written-for-it") }) {
        org.jetbrains.compose.web.dom.Text("Two of its dependencies were written for it")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two moving parts exist because this project needed them and nothing else fit.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/mongkn") {
                org.jetbrains.compose.web.dom.Text("mongkn")
            }
        }
        org.jetbrains.compose.web.dom.Text(" — a MongoDB driver for Kotlin/Native over the C driver via cinterop. There is no official one, and the native server build has to talk to the same database as the JVM build, in the same document shape. That constraint is what made the driver necessary rather than interesting.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/viddik") {
                org.jetbrains.compose.web.dom.Text("viddik")
            }
        }
        org.jetbrains.compose.web.dom.Text(" — the screenshot testing above.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Building the demo is what surfaced the gaps; the libraries are what came out of them. Which is roughly how every useful library I have written started.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-is-not") }) {
        org.jetbrains.compose.web.dom.Text("What it is not")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("There is no email confirmation, no password recovery, no rate limiting, and passwords are hashed with salted SHA-256 rather than a slow KDF.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Saying so is the point. A demo that quietly omits the security work invites someone to copy it, and the omissions are exactly what a reader cannot see. The public instance is a playground — please do not keep anything you would miss in it.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/mani") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/mani")
        }
        org.jetbrains.compose.web.dom.Text(" · live at ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://mani.kotlin.website") {
            org.jetbrains.compose.web.dom.Text("mani.kotlin.website")
        }
    }
}
